/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"
import { EMPTY } from "common/constants/misc"
import { NavigationContainer } from "containers/NavigationContainer"
import {
  MME_URL_PREFIX,
  ORDER_MANAGER_URL_PREFIX,
  ECOMMERCE_URL_PREFIX,
  FEEDBACK,
  STOREFRONT_COLLECTION_URL_PREFIX,
  LOCATIONS,
} from "constants/routes"
import I18n from "i18n-js"
import { FC, useEffect, useState } from "react"
import { Route, Switch } from "react-router-dom"

import { LocaleContent, LocaleContext } from "common/utils/i18n"
import { sessionInfo } from "utils/session"
import { storageHelper } from "utils/storage"
import { ChunkErrorModal } from "components/Error/ChunkErrorModal"
import StorybookActionButton from "components/StorybookActionButton"
import { isStorybookUICatalogEntryShown } from "utils/storybookHelpers"

const fetchLocale = async (locale: string, callback: (_: object) => void) => {
  try {
    const localModule = await import(`../../../public/locales/${locale}.ts`)
    callback(localModule.default)
  } catch (e) {
    // don't care the error
  }
}

export const HomeContainer: FC = () => {
  const [localeContent, setLocaleContent] = useState<LocaleContent>(EMPTY)
  const userLocale = storageHelper.getLang()
  const [locale, setLocale] = useState<string>(userLocale)
  const [isLocaleLoading, setIsLocaleLoading] = useState(true)
  // this state is only used to make `HomeContainer` refresh
  const [switchLocaleTriggered, setSwitchLocaleTriggered] = useState(0)
  const switchLocale = () => {
    setSwitchLocaleTriggered(val => val + 1)
  }
  useEffect(() => {
    const currentUserLocale = storageHelper.getLang()
    fetchLocale(currentUserLocale, (content: LocaleContent) => {
      I18n.locale = currentUserLocale
      I18n.translations[currentUserLocale] = content.troy_common as object
      setLocaleContent(content)
      setLocale(currentUserLocale)
      setIsLocaleLoading(false)
    })
  }, [switchLocaleTriggered])
  return (
    <LocaleContext.Provider
      value={{
        locale,
        switchLocale,
        localeContent,
      }}
    >
      <Switch>
        <Route
          path={[
            MME_URL_PREFIX,
            ORDER_MANAGER_URL_PREFIX,
            ECOMMERCE_URL_PREFIX,
            FEEDBACK,
            STOREFRONT_COLLECTION_URL_PREFIX,
            LOCATIONS,
          ]}
          render={props => (
            <NavigationContainer {...props} isLocaleLoading={isLocaleLoading} />
          )}
        />
      </Switch>
      <ChunkErrorModal />
      {isStorybookUICatalogEntryShown() && <StorybookActionButton />}
    </LocaleContext.Provider>
  )
}

export const Home: FC = () => {
  useEffect(() => {
    return () => {
      sessionInfo.generateSessionID()
    }
  }, [])
  return <HomeContainer />
}
